// src/components/pages/admin-dash/AdminMatchResultTopPrizes.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Badge from "../../ui/Badge";
import Icon from "../../ui/Icon";
import Ball, { BallsRow } from "../../ui/Ball";
import AdminTable from "./AdminTable";
import AdminMatchResultFilterBar, { SelectField } from "./AdminMatchResultFilterBar";
import { parameterLabel } from "../../../constants/parameterLabels";

/**
 * Admin report — highest-prize match results, most valuable first
 * (GET /admin/match-result/top-prizes). Roadmap item 3.3.
 */
const LIMIT_OPTIONS = [
  { value: "10", label: "Top 10" },
  { value: "20", label: "Top 20" },
  { value: "50", label: "Top 50" },
  { value: "100", label: "Top 100" },
];

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : "—";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const lotteryBadge = (t) => {
  const v = (t || "").toUpperCase();
  if (v === "MEGA_MILLIONS") return <Badge variant="gold">Mega Millions</Badge>;
  if (v === "POWER_BALL") return <Badge variant="blue">Powerball</Badge>;
  return <Badge variant="neutral">{v || "—"}</Badge>;
};

export default function AdminMatchResultTopPrizes() {
  const [typeLottery, setTypeLottery] = useState("");
  const [parameter, setParameter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [limit, setLimit] = useState("20");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [didSearch, setDidSearch] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchTopPrizes = async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit };
      if (typeLottery) params.typeLottery = typeLottery;
      if (parameter) params.parameter = parameter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axiosInstance.get("/admin/match-result/top-prizes", { params });
      // Backend already returns these ordered by prizeValueUsd DESC — stamp the
      // rank once here so it stays correct even if the admin re-sorts the table
      // by a different column (AdminTable doesn't expose row index to render()).
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((r, i) => ({ ...r, rank: i + 1 })));
      setDidSearch(true);
    } catch {
      setError("Failed to load top prizes.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTypeLottery(""); setParameter(""); setStartDate(""); setEndDate(""); setLimit("20");
    setRows([]); setDidSearch(false); setError("");
  };

  const columns = [
    { key: "rank", label: "#", sort: true, width: "44px",
      render: (r) => <span className="tabular-nums" style={{ color: "var(--txt-3)" }}>{r.rank}</span> },
    { key: "user", label: "User", sort: true, value: (r) => r.userName || "",
      render: (r) => (
        <div>
          <div style={{ color: "var(--txt)" }} className="font-medium">{r.userName || "—"}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>#{r.userId ?? "?"}</div>
        </div>
      ) },
    { key: "typeLottery", label: "Lottery", sort: true, value: (r) => r.typeLottery || "",
      render: (r) => lotteryBadge(r.typeLottery) },
    { key: "drawDate", label: "Draw date", sort: true,
      render: (r) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{fmtDate(r.drawDate)}</span> },
    { key: "hits", label: "Hits", sort: true, align: "right",
      render: (r) => <span className="tabular-nums">{r.hits ?? "—"}</span> },
    { key: "extraMatched", label: "Extra ball", sort: true, value: (r) => (r.extraMatched ? 1 : 0),
      render: (r) => r.extraMatched ? <Badge variant="teal">Matched</Badge> : <span style={{ color: "var(--txt-3)" }}>—</span> },
    { key: "prizeValueUsd", label: "Prize", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium" style={{ color: "var(--gold)" }}>{fmtUsd(r.prizeValueUsd)}</span> },
    { key: "winner", label: "Winner", sort: true, value: (r) => (r.winner ? 1 : 0),
      render: (r) => r.winner ? <Badge variant="gold">Jackpot</Badge> : <span style={{ color: "var(--txt-3)" }}>—</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Top prizes
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Highest-value match results, most valuable first. Click a row for the full breakdown.
        </p>
      </header>

      <AdminMatchResultFilterBar
        typeLottery={typeLottery} setTypeLottery={setTypeLottery}
        parameter={parameter} setParameter={setParameter}
        startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate}
        onSearch={fetchTopPrizes}
        onReset={handleReset}
        loading={loading}
        extraSlot={<SelectField label="Show" value={limit} onChange={setLimit} options={LIMIT_OPTIONS} />}
      />

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${rows.length} results` : undefined}
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon="trophy"
        emptyMessage={didSearch ? "No match results for this filter." : "Search to see results."}
        defaultSort={{ key: "prizeValueUsd", dir: "desc" }}
        rowKey="id"
        onRowClick={setSelected}
      />

      {selected && (
        <TopPrizeDetailModal row={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function TopPrizeDetailModal({ row, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg fade-in-up max-h-[90vh] overflow-y-auto"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div className="flex items-start justify-between gap-3 p-6" style={{ borderBottom: "1px solid var(--border)" }}>
          <div>
            <h2 className="font-semibold leading-tight" style={{ fontSize: 20, color: "var(--txt)" }}>
              {row.userName || "—"}
              <span className="text-[13px] ml-2" style={{ color: "var(--txt-3)" }}>#{row.userId}</span>
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {lotteryBadge(row.typeLottery)}
              {row.winner && <Badge variant="gold">Jackpot</Badge>}
              {row.extraMatched && <Badge variant="teal">Extra ball matched</Badge>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md transition-colors p-1.5"
            style={{ color: "var(--txt-2)" }}
            aria-label="Close"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <DetailField label="Prize" value={fmtUsd(row.prizeValueUsd)} accent />
            <DetailField label="Hits" value={row.hits} />
            <DetailField label="Draw date" value={fmtDate(row.drawDate)} />
            <DetailField label="Parameter" value={parameterLabel(row.parameter)} />
          </div>

          <div>
            <div className="lg-label mb-2">Generated numbers</div>
            <BallsRow>
              {(row.generatedNumbers || []).map((n, i) => (
                <Ball key={`g-${i}`} value={n} matched={(row.winningNumbers || []).includes(n)} />
              ))}
              {row.generatedExtraBall != null && (
                <Ball value={row.generatedExtraBall} variant="gold" matched={row.extraMatched} />
              )}
            </BallsRow>
          </div>

          <div>
            <div className="lg-label mb-2">Official winning numbers</div>
            <BallsRow>
              {(row.winningNumbers || []).map((n, i) => (
                <Ball key={`w-${i}`} value={n} />
              ))}
              {row.winningExtraBall != null && <Ball value={row.winningExtraBall} variant="gold" />}
            </BallsRow>
          </div>

          {row.matchedNumbersDetail && (
            <DetailField label="Match detail" value={row.matchedNumbersDetail} wrap />
          )}
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, accent, wrap }) {
  return (
    <div>
      <div className="lg-label mb-1">{label}</div>
      <div
        className={`text-[13.5px] ${wrap ? "" : "tabular-nums"}`}
        style={{ color: accent ? "var(--gold)" : "var(--txt)", fontWeight: accent ? 600 : 400, wordBreak: "break-word" }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}
